import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { GraduationCapIcon, MapPinIcon, PhoneIcon, MailIcon, CheckCircleIcon, BookOpenIcon, BriefcaseIcon, ChevronDownIcon, ChevronLeftIcon, AwardIcon, BuildingIcon, LoaderIcon } from 'lucide-react';
import { getUniversityBySlug, getProgramBySlug, University, UniversityProgram } from '../data/universities';
import { useFormState } from '../hooks/useFormState';
import { formatPhone, stripPhone } from '../utils/phoneFormat';
import { educationLevels } from '../data/degrees';
import { TrustSignals } from '../components/TrustSignals';
import { usePageMeta } from '../hooks/usePageMeta';
const startDateOptions = ['Immediately', 'Within 1 month', 'Within 3 months', 'Within 6 months', 'Within 1 year', 'Not sure yet'];
function getGradYearOptions(): string[] {
  const currentYear = new Date().getFullYear();
  const years: string[] = [];
  for (let y = currentYear + 2; y >= currentYear - 30; y--) {
    years.push(String(y));
  }
  return years;
}
const gradYearOptions = getGradYearOptions();
export function UniversityPage() {
  const {
    universitySlug,
    programSlug
  } = useParams<{
    universitySlug: string;
    programSlug?: string;
  }>();
  const navigate = useNavigate();
  const [university, setUniversity] = useState<University | null>(null);
  usePageMeta(university ? `${university.name} Online Programs | OnlineCollegeGuide` : 'College Programs | OnlineCollegeGuide', university ? `Explore online degree programs at ${university.name}. ${university.description?.slice(0, 120) || 'Compare programs and request free information.'}` : 'Explore online degree programs at accredited colleges and universities.', {
    ogImage: university?.logoUrl || 'https://images.unsplash.com/photo-1562774053-701939374585?w=1200&h=630&fit=crop&q=80',
    ogType: 'website'
  });
  const [selectedProgram, setSelectedProgram] = useState<UniversityProgram | null>(null);
  const [formStep, setFormStep] = useState(0); // 0=program select, 1=education, 2=zip/address, 3=contact
  const [zipLoading, setZipLoading] = useState(false);
  const [zipLookedUp, setZipLookedUp] = useState(false);
  const {
    formData,
    errors,
    updateFormData,
    updateMultiple,
    validateUniStep1,
    validateUniStep2,
    validateUniStep3
  } = useFormState();
  useEffect(() => {
    if (universitySlug) {
      const foundUni = getUniversityBySlug(universitySlug);
      if (foundUni) {
        setUniversity(foundUni);
        if (programSlug) {
          const foundProg = getProgramBySlug(foundUni, programSlug);
          if (foundProg) {
            setSelectedProgram(foundProg);
            if (formStep === 0) setFormStep(1);
          } else {
            setSelectedProgram(null);
          }
        } else {
          setSelectedProgram(null);
          setFormStep(0);
        }
      } else {
        setUniversity(null);
      }
    }
    window.scrollTo(0, 0);
  }, [universitySlug, programSlug]);
  const handleProgramChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newSlug = e.target.value;
    if (newSlug) {
      navigate(`/colleges/${universitySlug}/${newSlug}`, {
        replace: true
      });
    } else {
      navigate(`/colleges/${universitySlug}`, {
        replace: true
      });
    }
  };
  const lookupZip = useCallback(async (zip: string) => {
    if (zip.length < 5) return;
    setZipLoading(true);
    try {
      const res = await fetch(`https://api.zippopotam.us/us/${zip}`);
      if (res.ok) {
        const data = await res.json();
        const place = data.places?.[0];
        if (place) {
          updateMultiple({
            city: place['place name'] || '',
            state: place['state abbreviation'] || ''
          });
          setZipLookedUp(true);
        }
      } else {
        // Fallback: let user enter manually
        setZipLookedUp(true);
      }
    } catch {
      setZipLookedUp(true);
    } finally {
      setZipLoading(false);
    }
  }, [updateMultiple]);
  const handleStep1Next = () => {
    if (validateUniStep1()) setFormStep(2);
  };
  const handleStep2Next = () => {
    if (validateUniStep2()) {
      lookupZip(formData.zipCode);
      setFormStep(3);
    }
  };
  const handleStep3Submit = () => {
    if (validateUniStep3()) {
      const cleanedFormData = {
        ...formData,
        phone: stripPhone(formData.phone)
      };
      console.log('Lead submitted:', cleanedFormData);
      navigate('/thank-you', {
        state: {
          formData: cleanedFormData,
          partner: university ? {
            name: university.name,
            logo: university.logoUrl || '',
            primaryColor: '#14479E',
            accentColor: '#5FA0EE',
            backUrl: `/colleges/${university.slug}`,
            programTitle: selectedProgram?.title || ''
          } : undefined
        }
      });
    }
  };
  const totalSteps = 3;
  const progressPercent = formStep === 0 ? 0 : Math.round(formStep / totalSteps * 100);
  const selectClasses = (hasError: boolean) => `block w-full px-3 py-3 text-sm border rounded-md bg-white appearance-none cursor-pointer ${hasError ? 'border-red-400' : 'border-gray-300 focus:ring-navy focus:border-navy'}`;
  const inputClasses = (hasError: boolean) => `block w-full px-3 py-3 text-sm border rounded-md shadow-sm ${hasError ? 'border-red-400' : 'border-gray-300 focus:ring-navy focus:border-navy'}`;
  if (!university) {
    return <div className="min-h-[60vh] flex flex-col items-center justify-center bg-light px-4" data-id="element-3059" data-mp="3ejtet40aslb5">
        <h1 className="text-3xl font-bold text-navy mb-4" data-id="element-3060" data-mp="39mksqsc13x4m">
          University Not Found
        </h1>
        <p className="text-gray-600 mb-8" data-id="element-3061" data-mp="34oj9o7elrxt5">
          We couldn't find the university you're looking for.
        </p>
        <Link to="/" className="text-white bg-navy px-6 py-2 rounded-md hover:bg-navy-light transition-colors" data-id="element-3062" data-mp="30nfu7m0gw3sg">
          Return Home
        </Link>
      </div>;
  }
  const renderFormContent = () => {
    // Step 0: Just program selector (shown when no program selected)
    if (formStep === 0) {
      return <div className="space-y-4" data-id="element-3063" data-mp="2pxwszphc335i">
          <div data-id="element-3064" data-mp="2c19v9khk48yj">
            <label htmlFor="hero-program-select" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3065" data-mp="2c1i5t5q7j26k">
              Select a Program
            </label>
            <div className="relative" data-id="element-3066" data-mp="34ovxopf1t7jt">
              <select id="hero-program-select" value={programSlug || ''} onChange={handleProgramChange} className="block w-full px-3 py-3 text-sm border border-gray-300 rounded-md bg-gray-50 appearance-none cursor-pointer focus:ring-navy focus:border-navy font-medium text-gray-800" data-id="element-3067" data-mp="34pxlluh5xoiy">
                <option value="" data-id="element-3068" data-mp="2zx4fb261q84r">-- Choose a Program --</option>
                {(() => {
                const groups: Record<string, typeof university.programs> = {};
                university.programs.forEach(p => {
                  const area = p.focusArea === '4' ? 'Computer Technology' : p.focusArea === '1' ? 'Business' : 'Other';
                  if (!groups[area]) groups[area] = [];
                  groups[area].push(p);
                });
                return Object.entries(groups).map(([area, progs]) => <optgroup key={area} label={area} data-id="element-3069" data-mp="1prji418y189f">
                      {progs.map(p => <option key={p.slug} value={p.slug} data-id="element-3070" data-mp="1lrivvuflrsrt">
                          {p.title}
                        </option>)}
                    </optgroup>);
              })()}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-500" data-id="element-3071" data-mp="t2u44m0fszsq">
                <ChevronDownIcon className="h-4 w-4" data-id="element-3072" data-mp="1734vdq5nsgef" />
              </div>
            </div>
          </div>
          <p className="text-xs text-gray-500 text-center" data-id="element-3073" data-mp="120xfiphpkg6t">
            Select a program above to get started
          </p>
        </div>;
    }
    // Step 1: Education level, start date, grad year
    if (formStep === 1) {
      return <div className="space-y-4" data-id="element-3074" data-mp="2dk00ye0i949s">
          {/* Program selector */}
          <div data-id="element-3075" data-mp="2xll79jev5307">
            <label htmlFor="step1-program" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3076" data-mp="2xhnnqwodi9fs">
              Program
            </label>
            <div className="relative" data-id="element-3077" data-mp="36jn3zmrkd9ho">
              <select id="step1-program" value={programSlug || ''} onChange={handleProgramChange} className="block w-full px-3 py-2.5 text-sm border border-gray-200 rounded-md bg-gray-50 appearance-none cursor-pointer font-medium text-gray-700" data-id="element-3078" data-mp="36ilq7aj5y8w9">
                {(() => {
                const groups: Record<string, typeof university.programs> = {};
                university.programs.forEach(p => {
                  const area = p.focusArea === '4' ? 'Computer Technology' : p.focusArea === '1' ? 'Business' : 'Other';
                  if (!groups[area]) groups[area] = [];
                  groups[area].push(p);
                });
                return Object.entries(groups).map(([area, progs]) => <optgroup key={area} label={area} data-id="element-3079" data-mp="1zxt8il05ptqi">
                      {progs.map(p => <option key={p.slug} value={p.slug} data-id="element-3080" data-mp="1se1tx5n0g6he">
                          {p.title}
                        </option>)}
                    </optgroup>);
              })()}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-400" data-id="element-3081" data-mp="1in74xtidqss9">
                <ChevronDownIcon className="h-4 w-4" data-id="element-3082" data-mp="1eizftv80fati" />
              </div>
            </div>
          </div>

          <div className="border-t border-gray-100 pt-3" data-id="element-3083" data-mp="2b8yywjlsdrqc" />

          {/* Education Level */}
          <div data-id="element-3084" data-mp="26a5iojf47mmh">
            <label htmlFor="currentEducation" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3085" data-mp="22amzpt6utlma">
              Highest Level of Education
            </label>
            <select id="currentEducation" value={formData.currentEducation} onChange={e => updateFormData('currentEducation', e.target.value)} className={selectClasses(!!errors.currentEducation)} data-id="element-3086" data-mp="1xdwjn5v1uq0v">
              <option value="" data-id="element-3087" data-mp="lyrfryj6mlfo">-- Please Select --</option>
              {educationLevels.map(level => <option key={level} value={level} data-id="element-3088" data-mp="3e33w4ovzjy2w">
                  {level}
                </option>)}
            </select>
            {errors.currentEducation && <p className="mt-1 text-xs text-red-600" data-id="element-3089" data-mp="3mxs08bptr9q4">
                {errors.currentEducation}
              </p>}
          </div>

          {/* Start Date */}
          <div data-id="element-3090" data-mp="3i4ywnr63e7fm">
            <label htmlFor="startDate" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3091" data-mp="q1vw50uyaih">
              When Would You Like to Start?
            </label>
            <select id="startDate" value={formData.startDate} onChange={e => updateFormData('startDate', e.target.value)} className={selectClasses(!!errors.startDate)} data-id="element-3092" data-mp="3ryz6u7g8e2v7">
              <option value="" data-id="element-3093" data-mp="5pwm06ct6w2h">-- Please Select --</option>
              {startDateOptions.map(opt => <option key={opt} value={opt} data-id="element-3094" data-mp="2bj4q9ud5d79o">
                  {opt}
                </option>)}
            </select>
            {errors.startDate && <p className="mt-1 text-xs text-red-600" data-id="element-3095" data-mp="27j4dsf9lz659">{errors.startDate}</p>}
          </div>

          {/* Grad Year */}
          <div data-id="element-3096" data-mp="21e6gwc4e3q1n">
            <label htmlFor="gradYear" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3097" data-mp="21eoaf0zo3q5v">
              High School Graduation Year
            </label>
            <select id="gradYear" value={formData.gradYear} onChange={e => updateFormData('gradYear', e.target.value)} className={selectClasses(!!errors.gradYear)} data-id="element-3098" data-mp="1sluur2gb6orh">
              <option value="" data-id="element-3099" data-mp="1dqycdwescl0o">-- Please Select --</option>
              {gradYearOptions.map(yr => <option key={yr} value={yr} data-id="element-3100" data-mp="1iphx5thn7qrg">
                  {yr}
                </option>)}
            </select>
            {errors.gradYear && <p className="mt-1 text-xs text-red-600" data-id="element-3101" data-mp="19n8rbgxbklke">{errors.gradYear}</p>}
          </div>

          <div className="flex gap-3" data-id="element-3102" data-mp="19p3c8v8lcdoh">
            <button type="button" onClick={() => navigate(`/colleges/${universitySlug}`, {
            replace: true
          })} className="flex items-center justify-center px-4 py-3 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-3103" data-mp="2z1x3pi5wu3if">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-3104" data-mp="2xkrmpxmlhwx4" />
              Back
            </button>
            <button type="button" onClick={handleStep1Next} className="flex-1 bg-navy hover:bg-navy-light text-white font-bold py-3.5 px-6 rounded-md text-sm tracking-wider uppercase transition-colors duration-200 shadow-md" data-id="element-3105" data-mp="2sl51p20izcdr">
              Next
            </button>
          </div>
        </div>;
    }
    // Step 2: Zip code → city/state/address
    if (formStep === 2) {
      return <div className="space-y-4" data-id="element-3106" data-mp="3bkixzqlq8z7f">
          <div data-id="element-3107" data-mp="3bjhmxm4pocpp">
            <label htmlFor="zipCode" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3108" data-mp="3bj7rhj0wddcm">
              Zip Code
            </label>
            <input type="text" id="zipCode" value={formData.zipCode} onChange={e => {
            const val = e.target.value.replace(/\D/g, '').slice(0, 5);
            updateFormData('zipCode', val);
            setZipLookedUp(false);
            if (val.length === 5) {
              lookupZip(val);
            }
          }} className={inputClasses(!!errors.zipCode)} placeholder="12345" maxLength={5} data-id="element-3109" data-mp="7vz5k7xaqj05" />
            {errors.zipCode && <p className="mt-1 text-xs text-red-600" data-id="element-3110" data-mp="kdi7e4057474">{errors.zipCode}</p>}
            {zipLoading && <div className="mt-2 flex items-center text-xs text-gray-500" data-id="element-3111" data-mp="pefu8tw3kmey">
                <LoaderIcon className="w-3 h-3 mr-1 animate-spin" data-id="element-3112" data-mp="tfj3uynjxm7q" />
                Looking up your location...
              </div>}
          </div>

          {zipLookedUp && <>
              <div className="grid grid-cols-2 gap-3" data-id="element-3113" data-mp="134t26w8zo14a">
                <div data-id="element-3114" data-mp="135b3466lj3ij">
                  <label htmlFor="city" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3115" data-mp="136vwhsh6cget">
                    City
                  </label>
                  <input type="text" id="city" value={formData.city} onChange={e => updateFormData('city', e.target.value)} className="block w-full px-3 py-3 text-sm border border-gray-300 rounded-md shadow-sm focus:ring-navy focus:border-navy bg-gray-50" placeholder="City" data-id="element-3116" data-mp="1d4jtyq66020o" />
                </div>
                <div data-id="element-3117" data-mp="29tuero17i3kt">
                  <label htmlFor="state" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3118" data-mp="29s9i9svc0akb">
                    State
                  </label>
                  <input type="text" id="state" value={formData.state} onChange={e => updateFormData('state', e.target.value)} className="block w-full px-3 py-3 text-sm border border-gray-300 rounded-md shadow-sm focus:ring-navy focus:border-navy bg-gray-50" placeholder="State" data-id="element-3119" data-mp="2jkp1etid756s" />
                </div>
              </div>

              <div data-id="element-3120" data-mp="24t6bo3zpdwa8">
                <label htmlFor="address" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3121" data-mp="1zw68c0u8n7mt">
                  Street Address{' '}
                  <span className="text-gray-400 font-normal normal-case" data-id="element-3122" data-mp="3g8v0l0wip5do">
                    (optional)
                  </span>
                </label>
                <input type="text" id="address" value={formData.address} onChange={e => updateFormData('address', e.target.value)} className="block w-full px-3 py-3 text-sm border border-gray-300 rounded-md shadow-sm focus:ring-navy focus:border-navy" placeholder="123 Main St" data-id="element-3123" data-mp="3jru5xn0sknqi" />
              </div>
            </>}

          <div className="flex gap-3" data-id="element-3124" data-mp="3esh9cak4c2aq">
            <button type="button" onClick={() => setFormStep(1)} className="flex items-center justify-center px-4 py-3 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-3125" data-mp="3etiux8yrg76p">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-3126" data-mp="3stbhcs39818v" />
              Back
            </button>
            <button type="button" onClick={handleStep2Next} className="flex-1 bg-navy hover:bg-navy-light text-white font-bold py-3.5 px-6 rounded-md text-sm tracking-wider uppercase transition-colors duration-200 shadow-md" data-id="element-3127" data-mp="1mh4lwz6t8by">
              Next
            </button>
          </div>
        </div>;
    }
    // Step 3: Contact info + TCPA
    if (formStep === 3) {
      return <div className="space-y-4" data-id="element-3128" data-mp="13y8qfjz0q2j6">
          <div className="grid grid-cols-2 gap-3" data-id="element-3129" data-mp="z0714hb016z9">
            <div data-id="element-3130" data-mp="z0gtucnfhs8n">
              <label htmlFor="firstName" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3131" data-mp="yxun2f8d3grh">
                First Name
              </label>
              <input type="text" id="firstName" value={formData.firstName} onChange={e => updateFormData('firstName', e.target.value)} className={inputClasses(!!errors.firstName)} placeholder="Jane" data-id="element-3132" data-mp="pypm729bvbc5" />
              {errors.firstName && <p className="mt-1 text-xs text-red-600" data-id="element-3133" data-mp="g7cv4cvcsp76">{errors.firstName}</p>}
            </div>

            <div data-id="element-3134" data-mp="b89z1hxcptbc">
              <label htmlFor="lastName" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3135" data-mp="6b9td84eyskr">
                Last Name
              </label>
              <input type="text" id="lastName" value={formData.lastName} onChange={e => updateFormData('lastName', e.target.value)} className={inputClasses(!!errors.lastName)} placeholder="Doe" data-id="element-3136" data-mp="28vaio0qvsth" />
              {errors.lastName && <p className="mt-1 text-xs text-red-600" data-id="element-3137" data-mp="2901f605f1tbo">{errors.lastName}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3" data-id="element-3138" data-mp="2dyuodgdo6y61">
            <div data-id="element-3139" data-mp="2hzwtgdu9q4fx">
              <label htmlFor="email" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3140" data-mp="2hzemon9zc7tr">
                Email Address
              </label>
              <input type="email" id="email" value={formData.email} onChange={e => updateFormData('email', e.target.value)} className={inputClasses(!!errors.email)} placeholder="jane@example.com" data-id="element-3141" data-mp="1q8gxht6tn5d1" />
              {errors.email && <p className="mt-1 text-xs text-red-600" data-id="element-3142" data-mp="1z7c599piknig">{errors.email}</p>}
            </div>

            <div data-id="element-3143" data-mp="23zm1f7h82jt6">
              <label htmlFor="phone" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3144" data-mp="24056hnlrxdps">
                Phone Number
              </label>
              <input type="tel" id="phone" value={formData.phone} onChange={e => updateFormData('phone', formatPhone(e.target.value))} className={inputClasses(!!errors.phone)} placeholder="(555)123-4567" maxLength={13} data-id="element-3145" data-mp="1bhhrmpxdeux5" />
              {errors.phone && <p className="mt-1 text-xs text-red-600" data-id="element-3146" data-mp="3w3miuchk4l0b">{errors.phone}</p>}
            </div>
          </div>

          <div className="space-y-2" data-id="element-3147" data-mp="dtsrdf59ja2m">
            <label className="flex items-start gap-2.5 cursor-pointer" data-id="element-3148" data-mp="9ts8nib4scpr">
              <input type="checkbox" checked={formData.tcpaConsent} onChange={e => updateFormData('tcpaConsent', e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-gray-300 text-navy focus:ring-navy cursor-pointer flex-shrink-0" data-id="element-3149" data-mp="nu33sxjh4dgq" />
              <span className="text-xs text-gray-600 leading-relaxed" data-id="element-3150" data-mp="xiuruuleydmd">
                By submitting this form, you are giving your express written
                consent to be contacted regarding our services using email,
                telephone or text, including our use of automated technology for
                calls or texts to the number provided above. This consent is not
                required to purchase goods or services. and you may always call
                us directly.
              </span>
            </label>
            {errors.tcpaConsent && <p className="text-xs text-red-600" data-id="element-3151" data-mp="1c6ldbvy7yxp9">{errors.tcpaConsent}</p>}
          </div>

          <div className="flex gap-3" data-id="element-3152" data-mp="1c8xq7up4ehvg">
            <button type="button" onClick={() => setFormStep(2)} className="flex items-center justify-center px-4 py-3 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-3153" data-mp="1g7eyihka1yeg">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-3154" data-mp="17933828gucfo" />
              Back
            </button>
            <button type="button" onClick={handleStep3Submit} className="flex-1 bg-cta hover:bg-cta-dark text-white font-bold py-3.5 px-6 rounded-md text-sm tracking-wider uppercase transition-colors duration-200 shadow-md" data-id="element-3155" data-mp="sk6wc8i5b5gn">
              Submit
            </button>
          </div>
        </div>;
    }
    return null;
  };
  return <main className="flex-grow bg-light pb-16" data-id="element-3156" data-mp="oitpbv3bgw85">
      {/* College Header */}
      <header className="bg-white border-b border-gray-200" data-id="element-3157" data-mp="mmsq18s74j5f">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between" data-id="element-3158" data-mp="rngr6na52gdd">
          <Link to={`/colleges/${universitySlug}`} className="flex items-center" data-id="element-3159" data-mp="doh43k29y9jb">
            <img src="https://cdn.magicpatterns.com/uploads/kMxFqqE7MbeZ2oDSzfEEA9/GTC_LOGO_(1).webp" alt={`${university.name} logo`} className="h-14 md:h-16 w-auto object-contain" data-id="element-3160" data-mp="im0hyiv28kaq" />
          </Link>
          <div className="flex items-center gap-4 text-sm text-gray-600" data-id="element-3161" data-mp="1ae7vhaf9rgkw">
            <span className="hidden sm:inline-flex items-center" data-id="element-3162" data-mp="wmb97n0a80wf">
              <PhoneIcon className="w-4 h-4 mr-1.5 text-gray-400" data-id="element-3163" data-mp="wlrzgtkscith" />
              {university.phone}
            </span>
            <a href={`mailto:${university.email}`} className="hidden md:inline-flex items-center hover:text-navy transition-colors" data-id="element-3164" data-mp="11ja447rrsbvo">
              <MailIcon className="w-4 h-4 mr-1.5 text-gray-400" data-id="element-3165" data-mp="1u27awyks3bwn" />
              {university.email}
            </a>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="hero-bg relative overflow-hidden" data-id="element-3166" data-mp="1zf5mov0xucb9">
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none bg-no-repeat bg-cover bg-center mix-blend-soft-light opacity-70" style={{
        backgroundImage: "url('https://cdn.magicpatterns.com/uploads/8uFwSQxxc9xYA37moQruEX/ChatGPT_Image_May_6,_2026,_08_20_39_PM.png')"
      }} data-id="element-3167" data-mp="1zedzo8lepmwl" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 py-10 md:py-16" data-id="element-3168" data-mp="1khwszuo5wwgd">
          <div className="inline-flex items-center text-gold font-semibold tracking-wider text-sm uppercase mb-6" data-id="element-3169" data-mp="1ghme1sie6vpd">
            <BuildingIcon className="w-4 h-4 mr-2" data-id="element-3170" data-mp="1udq1n2yrg5sv" />
            {university.name} &middot; {university.location}
          </div>

          <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 items-start" data-id="element-3171" data-mp="35upsrv9dgelb">
            {/* Left: Program Info */}
            <div className="lg:w-1/2 flex flex-col justify-center" data-id="element-3172" data-mp="30utmtqh46d34">
              <AnimatePresence mode="wait" data-id="element-3173" data-mp="38wulatsi7v4d">
                <motion.div key={selectedProgram?.slug || 'overview'} initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.25
              }} data-id="element-3174" data-mp="38vsxrpuaxg5r">
                  <h1 className="text-3xl md:text-4xl lg:text-[2.75rem] font-extrabold text-white mb-5 leading-tight" data-id="element-3175" data-mp="2p93m97ibtn8k">
                    {selectedProgram ? selectedProgram.title : `Explore Programs at ${university.name}`}
                  </h1>
                  <p className="text-base md:text-lg text-gray-200 leading-relaxed mb-6" data-id="element-3176" data-mp="2z5gf6361rx29">
                    {selectedProgram ? selectedProgram.description : university.description}
                  </p>
                </motion.div>
              </AnimatePresence>

              {!selectedProgram && <div className="inline-flex items-center px-4 py-2 rounded-full bg-white/10 border border-white/20 text-white text-sm font-medium self-start" data-id="element-3177" data-mp="2bfvt3belkjor">
                  <ChevronDownIcon className="w-4 h-4 mr-2 animate-bounce" data-id="element-3178" data-mp="1yyul65axy0mj" />
                  Select a program to get started
                </div>}

              {selectedProgram && <div className="flex flex-wrap gap-3 text-sm text-gray-300" data-id="element-3179" data-mp="1u1kvcojn3my6">
                  {selectedProgram.keyFacts.slice(0, 2).map((fact, idx) => <span key={idx} className="inline-flex items-center bg-white/10 border border-white/15 rounded-full px-3 py-1" data-id="element-3180" data-mp="1kzbn65bug5ny" data-mpi={idx}>
                      <CheckCircleIcon className="w-3.5 h-3.5 text-green-400 mr-1.5" data-id="element-3181" data-mp="1g7bi5ygx2mki" />
                      {fact}
                    </span>)}
                </div>}
            </div>

            {/* Right: Form Card */}
            <div className="lg:w-1/2 w-full flex justify-center lg:justify-end" data-id="element-3182" data-mp="16bqiw838g0eg">
              <div className="w-full max-w-md" data-id="element-3183" data-mp="119j5de2r8ca0">
                <div className="bg-white rounded-xl shadow-2xl overflow-hidden border border-gray-100" data-id="element-3184" data-mp="x97mo6tycm16">
                  <div className="bg-navy px-6 py-3.5" data-id="element-3185" data-mp="1m0569aka9dvb">
                    <h2 className="text-white font-bold text-center text-sm tracking-widest uppercase" data-id="element-3186" data-mp="1m0n94q1gaqc7">
                      Request Information
                    </h2>
                  </div>

                  {/* Progress Bar */}
                  {formStep > 0 && <div className="px-6 pt-4" data-id="element-3187" data-mp="1h8n1scj1wv5l">
                      <div className="flex justify-between items-center mb-1.5" data-id="element-3188" data-mp="1h7c1imote5j8">
                        <span className="text-xs font-semibold text-navy uppercase tracking-wider" data-id="element-3189" data-mp="138m4t85wq7ve">
                          Step {formStep} of {totalSteps}
                        </span>
                        <span className="text-xs text-gray-500" data-id="element-3190" data-mp="u9qw9pfe4lox">
                          {progressPercent}%
                        </span>
                      </div>
                      <div className="h-1.5 w-full bg-gray-200 rounded-full overflow-hidden" data-id="element-3191" data-mp="2omzzx0l99cz9">
                        <motion.div className="h-full bg-gold rounded-full" initial={{
                      width: 0
                    }} animate={{
                      width: `${progressPercent}%`
                    }} transition={{
                      duration: 0.4,
                      ease: 'easeOut'
                    }} data-id="element-3192" data-mp="2so1zxjmzxt2a" />
                      </div>
                    </div>}

                  <div className="p-6" data-id="element-3193" data-mp="m01p201wfim5">
                    <AnimatePresence mode="wait" data-id="element-3194" data-mp="lz09bi9xu7y3">
                      <motion.div key={formStep} initial={{
                      opacity: 0,
                      x: 30
                    }} animate={{
                      opacity: 1,
                      x: 0
                    }} exit={{
                      opacity: 0,
                      x: -30
                    }} transition={{
                      duration: 0.2
                    }} data-id="element-3195" data-mp="ly8h8hfjusck">
                        {renderFormContent()}
                      </motion.div>
                    </AnimatePresence>
                  </div>
                </div>

                <p className="text-center text-gray-300 text-sm mt-3 italic" data-id="element-3196" data-mp="36jqq2u3xfvjw">
                  Used by{' '}
                  <strong className="text-white font-semibold" data-id="element-3197" data-mp="1agvsjd4zg6ku">
                    2M+ students
                  </strong>{' '}
                  to choose the right school.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <TrustSignals data-id="element-3198" data-mp="1ofbyh1v0tt1x" />

      {/* Program Content — Full Width */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-3199" data-mp="1te5ccve7zm2m">
        <AnimatePresence mode="wait" data-id="element-3200" data-mp="1tbswqovxpnsq">
          {selectedProgram ? <motion.div key={selectedProgram.slug} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} transition={{
          duration: 0.3
        }} className="space-y-8" data-id="element-3201" data-mp="1ybfaczfod622">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3202" data-mp="2c5fzr2kqlgpr">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sm:p-8 h-full" data-id="element-3203" data-mp="2h2om7fcx46wt">
                  <div className="flex items-center mb-6 border-b border-gray-100 pb-4" data-id="element-3204" data-mp="5g4lhka3w0j1">
                    <div className="p-2 bg-navy/5 rounded-lg mr-3" data-id="element-3205" data-mp="g9uzhu8tcyi">
                      <BriefcaseIcon className="w-6 h-6 text-navy" data-id="element-3206" data-mp="j4ff9d9axrx" />
                    </div>
                    <h2 className="text-xl font-bold text-navy" data-id="element-3207" data-mp="3smojad8eksoy">
                      Potential Job Opportunities
                    </h2>
                  </div>
                  <p className="text-sm text-gray-600 mb-4 font-medium" data-id="element-3208" data-mp="o52hyosw0agz">
                    {selectedProgram.jobOpportunitiesIntro}:
                  </p>
                  <ul className="space-y-3" data-id="element-3209" data-mp="fe251uh0t95b">
                    {selectedProgram.jobOpportunities.map((job, idx) => <li key={idx} className="flex items-start text-sm text-gray-700" data-id="element-3210" data-mp="aez16dcs18yx" data-mpi={idx}>
                        <span className="w-1.5 h-1.5 rounded-full bg-navy mt-1.5 mr-3 flex-shrink-0" data-id="element-3211" data-mp="ad4otnfkmp6g" />
                        <span data-id="element-3212" data-mp="133msoqo4cukx">{job}</span>
                      </li>)}
                  </ul>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sm:p-8 h-full" data-id="element-3213" data-mp="bgbm125uu4yz">
                  <div className="flex items-center mb-6 border-b border-gray-100 pb-4" data-id="element-3214" data-mp="g7a5gam53j6g">
                    <div className="p-2 bg-navy/5 rounded-lg mr-3" data-id="element-3215" data-mp="1i5m2q3lz8wr">
                      <AwardIcon className="w-6 h-6 text-navy" data-id="element-3216" data-mp="1fsvxpnp5s8m" />
                    </div>
                    <h2 className="text-xl font-bold text-navy" data-id="element-3217" data-mp="6h8i5l1m24sl">Key Facts</h2>
                  </div>
                  <ul className="space-y-4" data-id="element-3218" data-mp="u7ci9iflbmeu">
                    {selectedProgram.keyFacts.map((fact, idx) => <li key={idx} className="flex items-start text-sm text-gray-700" data-id="element-3219" data-mp="z54cwk7mdmmq" data-mpi={idx}>
                        <CheckCircleIcon className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" data-id="element-3220" data-mp="l53av1fmc95o" />
                        <span className="leading-tight pt-0.5" data-id="element-3221" data-mp="p65avkhd0p8p">{fact}</span>
                      </li>)}
                  </ul>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-3222" data-mp="39y74k1siy5a8">
                <div className="bg-navy px-6 py-4 flex items-center" data-id="element-3223" data-mp="32dn2p4w0wl9d">
                  <BookOpenIcon className="w-5 h-5 text-gold mr-3" data-id="element-3224" data-mp="32fzk9gxvctlw" />
                  <h2 className="text-lg font-bold text-white" data-id="element-3225" data-mp="2xejvbdskm2y8">
                    Program Curriculum
                  </h2>
                </div>
                <table className="min-w-full divide-y divide-gray-200" data-id="element-3226" data-mp="37diy4fqnn4db">
                  <thead className="bg-gray-50" data-id="element-3227" data-mp="37b6kgerxjg62">
                    <tr data-id="element-3228" data-mp="2jmvjzcq5biib">
                      <th scope="col" className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider" data-id="element-3229" data-mp="2jo6nrcjlcq9f">
                        Course Name
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-bold text-gray-500 uppercase tracking-wider" data-id="element-3230" data-mp="2te9pq28oxejw">
                        Credits
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100" data-id="element-3231" data-mp="20uugfx2ff42k">
                    {selectedProgram.curriculum.map((item, idx) => <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'} data-id="element-3232" data-mp="vebsfuvrgjd1" data-mpi={idx}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-3233" data-mp="qo54lx92p2vu">
                          {item.course}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right" data-id="element-3234" data-mp="lmo6ualvxwjh">
                          {item.credits}
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </motion.div> : <motion.div key="default-overview" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} className="space-y-8" data-id="element-3235" data-mp="19ew8ijaak12u">
              {/* Benefits of Online Learning */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sm:p-8" data-id="element-3236" data-mp="zfxbxz2svvw7">
                <div className="flex items-center mb-6 border-b border-gray-100 pb-4" data-id="element-3237" data-mp="3p6shk4dlsuvs">
                  <div className="p-2 bg-navy/5 rounded-lg mr-3" data-id="element-3238" data-mp="3k4urorap71tv">
                    <CheckCircleIcon className="w-6 h-6 text-navy" data-id="element-3239" data-mp="1zovjzz0jnijp" />
                  </div>
                  <h2 className="text-xl font-bold text-navy" data-id="element-3240" data-mp="24mx48n9phnwr">
                    Benefits of Online Learning with Global Tech College
                  </h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-3241" data-mp="2ed9lo6m850yo">
                  {[{
                icon: <AwardIcon className="w-6 h-6 text-crimson" data-id="element-3242" data-mp="2jcmhvhyzjkdx" />,
                title: 'High-Quality Education Experience'
              }, {
                icon: <BookOpenIcon className="w-6 h-6 text-crimson" data-id="element-3243" data-mp="2ncd7hicrzugv" />,
                title: 'Flexible Online Schedules'
              }, {
                icon: <CheckCircleIcon className="w-6 h-6 text-crimson" data-id="element-3244" data-mp="2xc453swmupl6" />,
                title: 'Accredited by ACCET'
              }, {
                icon: <BriefcaseIcon className="w-6 h-6 text-crimson" data-id="element-3245" data-mp="y01jod4g7l4v" />,
                title: 'Career Services Assistance'
              }, {
                icon: <GraduationCapIcon className="w-6 h-6 text-crimson" data-id="element-3246" data-mp="12zxpmhwphmn2" />,
                title: '1-1 Student Consulting'
              }, {
                icon: <BuildingIcon className="w-6 h-6 text-crimson" data-id="element-3247" data-mp="qwl8jt0v0jn4" />,
                title: 'Very Low Tuition Fees'
              }].map((item, idx) => <div key={idx} className="bg-gray-50 rounded-xl border border-gray-100 p-5 flex items-center gap-4" data-id="element-3248" data-mp="14ww3p897cke3" data-mpi={idx}>
                      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-white shadow-sm flex-shrink-0" data-id="element-3249" data-mp="zyul0ofoun35">
                        {item.icon}
                      </div>
                      <h3 className="font-bold text-navy text-sm leading-tight" data-id="element-3250" data-mp="19suvz6xnimjs">
                        {item.title}
                      </h3>
                    </div>)}
                </div>
              </div>

              {/* Highlights Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-3251" data-mp="1xlbfzn0y8olq">
                {[{
              icon: <AwardIcon className="w-6 h-6 text-crimson" data-id="element-3252" data-mp="1smgrc4gaw5tp" />,
              title: 'Title IV Approved',
              desc: 'Federal financial aid available for eligible students'
            }, {
              icon: <GraduationCapIcon className="w-6 h-6 text-crimson" data-id="element-3253" data-mp="2g80rl2utv0w0" />,
              title: 'VA Benefits',
              desc: 'Approved for veterans and dependents education benefits'
            }, {
              icon: <CheckCircleIcon className="w-6 h-6 text-crimson" data-id="element-3254" data-mp="276b4g97zao41" />,
              title: 'ACCET Accredited',
              desc: 'Accrediting Council for Continuing Education & Training'
            }, {
              icon: <BriefcaseIcon className="w-6 h-6 text-crimson" data-id="element-3255" data-mp="1xazo5mgx98u6" />,
              title: 'Financial Aid',
              desc: 'Available for students who qualify'
            }].map((item, idx) => <div key={idx} className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 text-center" data-id="element-3256" data-mp="1oi60b0net14p" data-mpi={idx}>
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-crimson/5 mb-3" data-id="element-3257" data-mp="19kvry062cbd7">
                      {item.icon}
                    </div>
                    <h3 className="font-bold text-navy text-sm mb-1" data-id="element-3258" data-mp="2hst31si4ujrm">
                      {item.title}
                    </h3>
                    <p className="text-xs text-gray-500" data-id="element-3259" data-mp="2cx4862h25r1n">{item.desc}</p>
                  </div>)}
              </div>

              {/* Available Programs */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sm:p-8" data-id="element-3260" data-mp="2346oht0bxw1g">
                <div className="flex items-center mb-6 border-b border-gray-100 pb-4" data-id="element-3261" data-mp="1y76fpac3if6n">
                  <div className="p-2 bg-navy/5 rounded-lg mr-3" data-id="element-3262" data-mp="1u4a4i4ovxlcq">
                    <BookOpenIcon className="w-6 h-6 text-navy" data-id="element-3263" data-mp="1u6w85t8nngpo" />
                  </div>
                  <h2 className="text-xl font-bold text-navy" data-id="element-3264" data-mp="1p7jg8u2xfjg8">
                    Available Programs
                  </h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-3265" data-mp="3jjq1ymtg5ims">
                  {university.programs.map(prog => <button key={prog.slug} onClick={() => navigate(`/colleges/${universitySlug}/${prog.slug}`, {
                replace: true
              })} className="flex items-start gap-3 p-4 rounded-lg border border-gray-200 hover:border-navy/30 hover:bg-navy/[0.02] transition-all text-left group" data-id="element-3266" data-mp="3eleqy8rf122z">
                      <div className="p-1.5 bg-navy/5 rounded-md group-hover:bg-navy/10 transition-colors mt-0.5" data-id="element-3267" data-mp="3vv0hdk8j2btf">
                        <GraduationCapIcon className="w-4 h-4 text-navy" data-id="element-3268" data-mp="4mmoqzmpv9v9" />
                      </div>
                      <div data-id="element-3269" data-mp="336uvuv9ngisj">
                        <h3 className="font-semibold text-navy text-sm group-hover:text-navy-light transition-colors" data-id="element-3270" data-mp="3844wz86eiiw7">
                          {prog.title}
                        </h3>
                        <p className="text-xs text-gray-500 mt-1" data-id="element-3271" data-mp="3d5ki108kwpel">
                          {prog.degreeLevel === '1' ? 'Certificate' : 'Associate Degree'}{' '}
                          · {prog.keyFacts[0]}
                        </p>
                      </div>
                    </button>)}
                </div>
              </div>

              {/* Accreditation */}
              <div className="bg-navy/5 rounded-xl border border-navy/10 p-6 flex items-start gap-4" data-id="element-3272" data-mp="2tdn7t5kjgrv7">
                <div className="p-2 bg-white rounded-lg shadow-sm" data-id="element-3273" data-mp="2oflnyifagoio">
                  <AwardIcon className="w-6 h-6 text-navy" data-id="element-3274" data-mp="32g6anpobr4hg" />
                </div>
                <div data-id="element-3275" data-mp="2yekrxnp3zvzc">
                  <h3 className="font-bold text-navy mb-1" data-id="element-3276" data-mp="2yfclksz5bjmz">Accreditation</h3>
                  <p className="text-sm text-gray-600" data-id="element-3277" data-mp="2ao76ig114vdq">
                    The Accrediting Council for Continuing Education & Training
                    (ACCET)
                  </p>
                  <p className="text-sm text-gray-500 mt-1" data-id="element-3278" data-mp="2jh0s0v72kr01">
                    {university.location}
                  </p>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </main>;
}